import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Search, CheckSquare, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { Button, Select } from '../../../components';
import { qualityService } from '../../../services';
import {
  Badge, Card, EmptyState, actionStatus, formatDate, initials, inputClass, errorMessage,
} from './shared';
import ActionFormModal from './ActionFormModal';

/**
 * Follow-up actions, across every audit.
 *
 * This list deliberately includes actions from COMPLETED audits. Signing an
 * audit off records what was found; it does not finish the work that the
 * findings raised, and in practice that is exactly where outstanding work
 * hides. A list that dropped them would quietly lose the follow-ups a manager
 * most needs to see.
 *
 * Defaults to Open and sorts by due date, because the question this screen
 * answers is "what is outstanding and what is late", not "what exists".
 */
const FollowUpActions = ({ meta, scopeAuditId, scopeTitle, onBack, onOpenAudit, onChanged }) => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await qualityService.getActions(scopeAuditId);
      setActions(response.data || []);
    } catch (error) {
      toast.error(errorMessage(error, 'Actions could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [scopeAuditId]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return actions
      .filter((a) => {
        if (filter === 'all') return true;
        if (filter === 'overdue') return a.overdue;
        return a.status === filter;
      })
      .filter((a) => !needle || `${a.title} ${a.owner_name || ''}`.toLowerCase().includes(needle))
      .sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)));
  }, [actions, filter, search]);

  const remove = async (action) => {
    if (!window.confirm('Remove this action? This is for one raised by mistake.')) return;
    try {
      await qualityService.deleteAction(action.id);
      toast.success('Action removed.');
      load();
      onChanged?.();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  return (
    <Card>
      <div className="px-6 pt-6 pb-2">
        <button
          type="button"
          onClick={() => onBack()}
          className="inline-flex items-center gap-2 text-[12px] text-[#6B83A4] dark:text-gray-400 hover:underline mb-5"
        >
          <ArrowLeft className="w-4 h-4" /> Back to audits
        </button>
        <h2 className="text-[17px] font-bold text-[#0B1220] dark:text-gray-100">Follow-up actions</h2>
        <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-1.5">
          {scopeAuditId
            ? scopeTitle || 'Actions raised by this audit.'
            : 'Outstanding work from all audits, including completed audits.'}
        </p>
      </div>

      <div className="flex items-center gap-2.5 flex-wrap px-6 py-5">
        <div className="relative flex-1 min-w-[180px] max-w-[320px]">
          <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            className={`${inputClass} pl-9 py-2`}
            placeholder="Search actions…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search actions"
          />
        </div>
        <Select
          value={filter}
          onChange={setFilter}
          size="sm"
          className="w-[180px]"
          options={[
            { value: 'open', label: 'Open actions' },
            { value: 'overdue', label: 'Overdue actions' },
            { value: 'done', label: 'Completed actions' },
            { value: 'all', label: 'All actions' },
          ]}
        />
        {scopeAuditId && (
          <Button size="sm" variant="secondary" onClick={() => onBack('actions')}>Show all actions</Button>
        )}
        <div className="flex-1" />
        <Button size="sm" variant="secondary" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setModal({})}>
          Add action
        </Button>
      </div>

      {loading ? (
        <p className="py-16 text-center text-[13px] text-[#64748B] dark:text-gray-400">Loading actions…</p>
      ) : !rows.length ? (
        <EmptyState
          icon={CheckSquare}
          title={actions.length ? 'No matching actions' : 'No follow-up actions'}
          description={actions.length
            ? 'Try another filter, or add a follow-up action.'
            : 'Actions appear here when an audit check needs attention.'}
          action={actions.length
            ? <Button variant="secondary" onClick={() => { setSearch(''); setFilter('all'); }}>Clear filters</Button>
            : null}
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="border-t border-[#E3E9F3] dark:border-gray-800">
                  {['Action', 'Owner', 'Due date', 'Status', ''].map((heading, i) => (
                    <th
                      key={heading || i}
                      className={`bg-[#F8FAFD] dark:bg-gray-800/40 text-[11px] font-semibold text-[#8391A7] dark:text-gray-400 text-left py-3.5 px-5 ${i === 0 ? 'pl-6' : ''} ${i === 4 ? 'pr-6' : ''}`}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((action) => {
                  const state = actionStatus(action);
                  return (
                    <tr key={action.id} className="border-t border-[#EEF2F8] dark:border-gray-800 hover:bg-[#FCFDFF] dark:hover:bg-gray-800/30">
                      <td className="py-4 px-5 pl-6 align-top w-[40%]">
                        <span className="block text-[13px] font-semibold text-[#0B1220] dark:text-gray-100">{action.title}</span>
                        {action.audit ? (
                          <button
                            type="button"
                            onClick={() => onOpenAudit(action.audit.id)}
                            className="text-[11px] text-[#6686B6] dark:text-blue-400 hover:underline mt-1.5 text-left"
                          >
                            {action.audit.title}
                          </button>
                        ) : (
                          <span className="block text-[11px] text-[#94A3B8] dark:text-gray-500 mt-1.5">General follow-up</span>
                        )}
                      </td>
                      <td className="py-4 px-5 align-top">
                        <span className="inline-flex items-center gap-2 text-[12.5px] text-[#50627D] dark:text-gray-300">
                          <span className="w-7 h-7 rounded-full bg-[#EEF3FF] dark:bg-blue-900/25 text-blue-600 text-[10px] font-bold flex items-center justify-center shrink-0">
                            {initials(action.owner_name)}
                          </span>
                          {action.owner_name || 'Unassigned'}
                        </span>
                      </td>
                      <td className="py-4 px-5 align-top text-[12.5px]">
                        <span className={action.overdue ? 'text-rose-600 dark:text-rose-400 font-semibold' : 'text-[#50627D] dark:text-gray-300'}>
                          {formatDate(action.due_date)}
                        </span>
                      </td>
                      <td className="py-4 px-5 align-top"><Badge tone={state.tone}>{state.label}</Badge></td>
                      <td className="py-4 px-5 pr-6 align-top text-right whitespace-nowrap">
                        <Button
                          size="sm"
                          variant="secondary"
                          rightIcon={<ChevronRight className="w-4 h-4" />}
                          onClick={() => setModal({ action })}
                        >
                          {action.status === 'done' ? 'View' : 'Update'}
                        </Button>
                        {action.status === 'open' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="ml-1"
                            icon={<Trash2 className="w-4 h-4" />}
                            aria-label="Remove action"
                            onClick={() => remove(action)}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-6 py-4 border-t border-[#E3E9F3] dark:border-gray-800 text-[11.5px] text-[#8192AC] dark:text-gray-500">
            <span>{rows.length} {rows.length === 1 ? 'action' : 'actions'}</span>
            <span>Earliest due first</span>
          </div>
        </>
      )}

      {modal && (
        <ActionFormModal
          open
          onClose={() => setModal(null)}
          meta={meta}
          action={modal.action}
          // Adding an action while the list is scoped to one audit links it to
          // that audit — the manager is looking at its follow-ups, so a new
          // one belongs to it rather than floating free.
          audit={scopeAuditId && !modal.action ? { id: scopeAuditId, title: scopeTitle } : null}
          onSaved={() => { load(); onChanged?.(); }}
        />
      )}
    </Card>
  );
};

export default FollowUpActions;
